import React, { useEffect } from "react";
import { NavLink } from "react-router-dom";
import cn from "classnames";
import { useDispatch } from "react-redux";
import { useTranslation } from "react-i18next";

import MailerLiteForm from "../mailerLiteForm";
import { actions as mainActions } from "../../actions/main";
import { socialName } from "../../constants/main";
import { ReactComponent as FacebookIcon } from "../../media/icons/footer/facebook.svg";
import { ReactComponent as LinkedinIcon } from "../../media/icons/footer/linkedin.svg";
import { ReactComponent as TwitterIcon } from "../../media/icons/footer/twitter.svg";
import { ReactComponent as GoogleIcon } from "../../media/icons/footer/google.svg";
import { ReactComponent as RSSIcon } from "../../media/icons/footer/rss.svg";
import { ReactComponent as FooterLogo } from "../../media/icons/newFooterLogo.svg";
import instagramIcon from "../../media/icons/footer/instagram.svg";
import styles from "./footer.module.css";

const SOCIAL_LABELS = {
  [socialName.facebook]: "Facebook",
  [socialName.facebookGroup]: "Facebook",
  [socialName.linkedin]: "LinkedIn",
  [socialName.linkedinGroup]: "LinkedIn",
  [socialName.twitter]: "X (Twitter)",
  [socialName.google]: "Google",
  [socialName.rss]: "RSS feed",
  [socialName.instagram]: "Instagram"
};

const Footer = ({ footerInfo, socialLinks }) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();

  useEffect(() => {
    if (!footerInfo?.length || !socialLinks?.length) {
      dispatch(mainActions.getFooterInfo());
    }
  }, [dispatch, footerInfo?.length, socialLinks?.length]);

  if (!footerInfo?.length || !socialLinks?.length) {
    return null;
  }

  const sliceFooter = () => {
    const midIndex = Math.ceil(footerInfo.length / 2);
    const firstHalfFooter = footerInfo.slice(0, midIndex);
    const secondHalfFooter = footerInfo.slice(midIndex);

    return [firstHalfFooter, secondHalfFooter];
  };

  const [firstHalfFooter, secondHalfFooter] = sliceFooter();

  const getFullYear = () => new Date().getFullYear();

  const renderFooter = footerLinks => {
    return footerLinks.map(item => (
      <React.Fragment key={`${item.url}-${item.title}}`}>
        {item.url.includes("https") || item.url.includes("www.") ? (
          // eslint-disable-next-line
          <a href={item.url} target="_blank" className={styles.navItem} rel="noopener noreferrer">
            {item.title}
          </a>
        ) : (
          // eslint-disable-next-line
          <NavLink href={item.url} to={item.url} className={styles.navItem}>
            {item.title}
          </NavLink>
        )}
      </React.Fragment>
    ));
  };

  const renderSocialsImage = type => {
    if (type === socialName.facebook || type === socialName.facebookGroup) {
      return <FacebookIcon aria-hidden="true" />;
    }

    if (type === socialName.linkedin || type === socialName.linkedinGroup) {
      return <LinkedinIcon aria-hidden="true" />;
    }

    if (type === socialName.twitter) {
      return <TwitterIcon aria-hidden="true" />;
    }

    if (type === socialName.google) {
      return <GoogleIcon aria-hidden="true" />;
    }
    if (type === socialName.rss) {
      return <RSSIcon aria-hidden="true" />;
    }

    if (type === socialName.instagram) {
      return <img src={instagramIcon} alt="" />;
    }

    return <></>;
  };

  const renderSocials = () => {
    return (
      <>
        {socialLinks
          .filter(item => Boolean(item.social_url) && SOCIAL_LABELS[item.social_name])
          .map(item => {
            return (
              <a
                key={item.social_name}
                className={styles.link}
                href={item.social_url}
                rel="noopener noreferrer"
                target="_blank"
                aria-label={t("dashboard_footer_social_link", { network: SOCIAL_LABELS[item.social_name] })}
              >
                {renderSocialsImage(item.social_name)}
              </a>
            );
          })}
      </>
    );
  };

  return (
    <footer className={styles.footer}>
      <div className={styles.content}>
        <div className={styles.logoContent}>
          <div>
            <div>
              <FooterLogo className={styles.logo} />
              <h2 className={styles.footerDescription}>Stay connected to your communities</h2>
            </div>

            <div className={cn(styles.socials, styles.socialsTablet)}>{renderSocials()}</div>
          </div>

          <div className={cn(styles.form, styles.formTablet)}>
            <MailerLiteForm id="mailerLiteForm1" />
          </div>
        </div>

        <nav className={styles.nav} aria-label={t("dashboard_footer_nav_label")}>
          <div className={styles.navContent}>
            <span className={styles.navItemHeading}>Heritage Web</span>
            {renderFooter(firstHalfFooter)}
          </div>
          <div className={styles.navContent}>
            <span className={styles.navItemHeading}>Find</span>
            {renderFooter(secondHalfFooter)}
          </div>
        </nav>
      </div>

      <div className={cn(styles.form, styles.formMobile)}>
        <MailerLiteForm id="mailerLiteForm2" />
      </div>
      <div className={styles.bottomContent}>
        <div className={cn(styles.socials, styles.socialsMobileAndDesktop)}>{renderSocials()}</div>
        <p className={styles.copy}>
          Copyright ©{" "}
          <a href="https://heritageweb.com/" target="_blank" rel="noopener noreferrer">
            Heritage Web LLC
          </a>{" "}
          2011-{`${getFullYear()}. `}
          <br />
          All Rights Reserved.
        </p>
      </div>
    </footer>
  );
};

export default React.memo(Footer);
